<script setup>
import SoftwareCardIssue from '@/StoreWeb/components/miscellaneous/conduent-support-reader/software/errors/SoftwareCardIssue'
import SoftwareConnectionLost from '@/StoreWeb/components/miscellaneous/conduent-support-reader/software/errors/SoftwareConnectionLost'
import * as anonymousMutationTypes from '@/StoreWeb/store/modules/anonymous/mutation-types'
import * as cardReaderActionTypes from '@/StoreWeb/store/modules/card-reader/action-types'
import config from 'config'
import { computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const emit = defineEmits(['descriptionUpdated', 'componentUpdated', 'propsUpdated', 'setErrorOnWriteOperation', 'stateUpdated', 'softwareReady'])

const props = defineProps({
    operationType: {
        type: String,
        default: 'READ',
        validator: (val) => ['READ', 'WRITE'].includes(val)
    },
    supportId: {
        type: String,
        default: ''
    }
})

const store = useStore()
const { t } = useI18n()

const debugInformationsDisplay = computed(() => store.state.debuggingModule.debugInformationsDisplay)

function cleanStep () {
    emit('stateUpdated', '')
    emit('descriptionUpdated', '')
    emit('propsUpdated', null)
    emit('componentUpdated', null)
}

/* ------------------------------------------------------------- */
/*                          DETECT CARD                          */
/* ------------------------------------------------------------- */
function detectCard () {
    cleanStep()
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:detect_card:detect_in_progress:message'))
    window.detectCard()
}

function onDetectCardHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === -1) {
        emit('stateUpdated', 'error')
        emit('componentUpdated', SoftwareConnectionLost)
    }
}

async function onEndDetectCardHandler (event) {
    cleanStep()

    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response >= 1) {
        emit('descriptionUpdated', t('card_reader:detect_card:detection:success:message'))
        getCardSerialNumber()
    } else {
        emit('stateUpdated', 'error')
        emit('propsUpdated', {
            description: t('card_reader:detect_card:not_found:instructions:message'),
            title: t('card_reader:detect_card:card_not_found:message')
        })
        emit('componentUpdated', SoftwareCardIssue)
    }
}

function getCardSerialNumber () {
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:detect_card:get_card_id_in_progress:message'))
    window.GetCardSerialNumber()
}

function onGetCardSerialNumberHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === -1) {
        emit('stateUpdated', 'error')
        emit('componentUpdated', SoftwareConnectionLost)
    }
}

async function onEndGetCardSerialNumberHandler (event) {
    cleanStep()

    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (isNaN(event.detail.response) || parseInt(event.detail.response) !== -1) {
        if (props.supportId && event.detail.response !== props.supportId) {
            emit('stateUpdated', 'error')
            emit('propsUpdated', {
                description: t('card_reader:detect_card:wrong_card:instructions:message'),
                supportId: props.supportId,
                title: t('card_reader:detect_card:wrong_card:message')
            })
            emit('componentUpdated', SoftwareCardIssue)
        } else {
            emit('descriptionUpdated', t('card_reader:detect_card:get_card_id:success:message').replace('%cardId%', event.detail.response))
            store.commit(anonymousMutationTypes.SET_PLUGGED_SUPPORT_ID, event.detail.response)

            if (props.operationType === 'READ') {
                emit('stateUpdated', 'completed')
            }

            await store.dispatch(cardReaderActionTypes.GET_OPERATION, {
                operationType: 'READ',
                silent: true
            }).then(async (operationResponse) => {
                emit('softwareReady', {
                    operation: operationResponse?.data,
                    operationId: operationResponse?.data?.operationId,
                    operationType: 'READ',
                    url: operationResponse?.data?.serverHostname,
                    port: operationResponse?.data?.serverPort
                })
            }).catch((error) => {
                emit('stateUpdated', 'error')
                emit('propsUpdated', {
                    description: error,
                    title: t('card_reader:operation:error:message')
                })
                emit('componentUpdated', SoftwareCardIssue)
            })
        }
    } else {
        emit('stateUpdated', 'error')
        emit('propsUpdated', {
            description: t('card_reader:detect_card:not_recognized:instructions:message'),
            title: t('card_reader:detect_card:not_recognized:message')
        })
        emit('componentUpdated', SoftwareCardIssue)
    }
}

/* ------------------------------------------------------------- */
/*                     MOUNTED / UNMOUNTED                       */
/* ------------------------------------------------------------- */
onMounted(() => {
    window.conduentMobilitySolutionsUrl = config.support_reader.conduent_mobility_solutions_url
    document.body.addEventListener('card-reader-detect-card', onDetectCardHandler)
    document.body.addEventListener('card-reader-end-detect-card', onEndDetectCardHandler)
    document.body.addEventListener('card-reader-get-card-serial-number', onGetCardSerialNumberHandler)
    document.body.addEventListener('card-reader-end-get-card-serial-number', onEndGetCardSerialNumberHandler)
})
onUnmounted(() => {
    document.body.removeEventListener('card-reader-detect-card', onDetectCardHandler)
    document.body.removeEventListener('card-reader-end-detect-card', onEndDetectCardHandler)
    document.body.removeEventListener('card-reader-get-card-serial-number', onGetCardSerialNumberHandler)
    document.body.removeEventListener('card-reader-end-get-card-serial-number', onEndGetCardSerialNumberHandler)
})

/* ------------------------------------------------------------- */
/*                        EXPOSED METHODS                        */
/* ------------------------------------------------------------- */
defineExpose({ detectCard })
</script>
